import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

const ProjectFinanceDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [project, setProject] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState(null);
  const [dateRange, setDateRange] = useState({
    debut: new Date().getFullYear() + '-01-01',
    fin: new Date().getFullYear() + '-12-31'
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [dashRes, projectRes] = await Promise.all([
        axios.get(`${API_URL}/project-finances/${projectId}/dashboard?date_debut=${dateRange.debut}&date_fin=${dateRange.fin}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const projectData = projectRes.data.project || projectRes.data;
      setProject(projectData);
      
      // Vérifier si le projet est suspendu
      if (projectData.is_suspended) {
        setIsSuspended(true);
        setSuspensionReason(projectData.suspension_reason);
        setLoading(false);
        return;
      }
      
      setDashboard(dashRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId, dateRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    // Déterminer si c'est un nouveau collaborateur sans parts définies
    const isNewCollaborator = error.includes('non autorisé') || error.includes('Accès');
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md">
          <div className="text-6xl mb-4">{isNewCollaborator ? '⏳' : '🚫'}</div>
          <h2 className="text-xl font-bold text-gray-800 mb-3">
            {isNewCollaborator ? 'Accès en attente' : 'Accès refusé'}
          </h2>
          
          {isNewCollaborator ? (
            <div className="space-y-3 text-gray-600">
              <p>
                Vous êtes membre de ce projet, mais vos <strong>parts d'action</strong> n'ont pas encore été définies par l'auteur.
              </p>
              <div className="bg-indigo-50 rounded-lg p-4 text-left">
                <p className="text-sm font-medium text-indigo-800 mb-2">💡 Que se passe-t-il ?</p>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• L'auteur doit d'abord configurer votre pourcentage de bénéfices</li>
                  <li>• Une fois vos parts définies, vous pourrez accéder aux finances</li>
                  <li>• Contactez l'auteur du projet si vous avez des questions</li>
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                Vous recevrez une notification lorsque vos parts seront configurées.
              </p>
            </div>
          ) : (
            <p className="text-gray-600">{error}</p>
          )}
          
          <div className="mt-6 space-y-2">
            <Link 
              to={`/project/${projectId}`} 
              className="block w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              ← Retour au projet
            </Link>
            <Link 
              to="/projects" 
              className="block w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Voir tous les projets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Écran de blocage pour projet suspendu
  if (isSuspended) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-lg">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Projet Suspendu
          </h2>
          
          <p className="text-gray-600 mb-4">
            L'accès aux finances de ce projet a été temporairement suspendu.
          </p>
          
          {suspensionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-medium text-red-800 mb-1">Motif de suspension :</p>
              <p className="text-red-700">{suspensionReason}</p>
            </div>
          )}
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>💡 Note :</strong> Pour plus d'informations sur cette suspension ou pour demander une réactivation, veuillez contacter l'équipe d'administration de la plateforme.
            </p>
          </div>
          
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/projects')}
              className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
            >
              Voir mes autres projets
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const { access = {}, summary = {}, monthly_evolution = [], revenue_breakdown = [], expense_breakdown = [], objectives = [], recent_transactions = [] } = dashboard;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <Link to={`/project/${projectId}`} className="text-indigo-200 hover:text-white text-sm mb-2 inline-flex items-center gap-1">
                ← Retour au projet
              </Link>
              <h1 className="text-3xl font-bold">{project?.titre}</h1>
              <p className="text-indigo-200 mt-1">Gestion Financière</p>
              {!access.canManage && (
                <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  👁️ Mode visualisation ({access.role})
                </span>
              )}
            </div>
            
            {/* Date Range Selector */}
            <div className="flex items-center gap-2 bg-white/10 rounded-xl p-2">
              <input
                type="date"
                value={dateRange.debut}
                onChange={(e) => setDateRange(prev => ({ ...prev, debut: e.target.value }))}
                className="bg-white/20 border-0 rounded-lg px-3 py-2 text-white placeholder-white/60 focus:ring-2 focus:ring-white"
              />
              <span>→</span>
              <input
                type="date"
                value={dateRange.fin}
                onChange={(e) => setDateRange(prev => ({ ...prev, fin: e.target.value }))}
                className="bg-white/20 border-0 rounded-lg px-3 py-2 text-white placeholder-white/60 focus:ring-2 focus:ring-white"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link
            to={`/projects/${projectId}/finances`}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium"
          >
            📊 Dashboard
          </Link>
          <Link
            to={`/projects/${projectId}/finances/collaborators`}
            className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm"
          >
            👥 Collaborateurs
          </Link>
          <Link
            to={`/projects/${projectId}/finances/payments`}
            className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm"
          >
            💳 Paiements
          </Link>
          <Link
            to={`/projects/${projectId}/finances/reports`}
            className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm"
          >
            📈 Bilans
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Chiffre d'Affaires</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(summary.total_revenus)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💵</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Dépenses</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(summary.total_depenses)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💸</span>
              </div>
            </div>
          </div>

          <div className={`bg-white rounded-2xl shadow-sm p-6 border-l-4 ${summary.benefice_net >= 0 ? 'border-blue-500' : 'border-orange-500'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Bénéfice Net</p>
                <p className={`text-2xl font-bold mt-1 ${summary.benefice_net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {formatMoney(summary.benefice_net)}
                </p>
              </div>
              <div className={`w-12 h-12 ${summary.benefice_net >= 0 ? 'bg-blue-100' : 'bg-orange-100'} rounded-xl flex items-center justify-center`}>
                <span className="text-2xl">{summary.benefice_net >= 0 ? '📈' : '📉'}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Marge</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.marge}%</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Evolution Chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📈 Évolution Mensuelle</h3>
            {monthly_evolution.length > 0 ? (
              <div className="space-y-4">
                {monthly_evolution.map((month, idx) => {
                  const maxValue = Math.max(...monthly_evolution.map(m => Math.max(m.revenus, m.depenses)));
                  const revenueWidth = maxValue > 0 ? (month.revenus / maxValue) * 100 : 0;
                  const expenseWidth = maxValue > 0 ? (month.depenses / maxValue) * 100 : 0;
                  
                  return (
                    <div key={idx} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-700">{month.mois}</span>
                        <span className={`font-semibold ${month.benefice >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {month.benefice >= 0 ? '+' : ''}{formatMoney(month.benefice)}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Revenus</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-green-400 to-green-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${revenueWidth}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600 w-20 text-right">{formatMoney(month.revenus)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16">Dépenses</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-red-400 to-red-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${expenseWidth}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600 w-20 text-right">{formatMoney(month.depenses)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">📭</span>
                <p className="mt-2">Aucune donnée pour cette période</p>
              </div>
            )}
          </div>

          {/* Objectives */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">🎯 Objectifs</h3>
              {access.canManage && (
                <Link 
                  to={`/projects/${projectId}/finances/objectives`}
                  className="text-indigo-600 text-sm hover:underline"
                >
                  Gérer
                </Link>
              )}
            </div>
            {objectives.length > 0 ? (
              <div className="space-y-4">
                {objectives.slice(0, 5).map((obj, idx) => {
                  const progress = obj.valeur_cible > 0 ? Math.min((obj.valeur_actuelle / obj.valeur_cible) * 100, 100) : 0;
                  const statusColors = {
                    en_cours: 'bg-blue-500',
                    atteint: 'bg-green-500',
                    non_atteint: 'bg-red-500',
                    abandonne: 'bg-gray-400'
                  };
                  
                  return (
                    <div key={idx} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-800 text-sm">{obj.nom}</span>
                        <span className={`px-2 py-0.5 text-xs text-white rounded-full ${statusColors[obj.statut]}`}>
                          {obj.statut === 'en_cours' ? 'En cours' : obj.statut === 'atteint' ? 'Atteint ✓' : obj.statut}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`${statusColors[obj.statut]} h-2 rounded-full transition-all duration-500`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">
                          {obj.valeur_actuelle.toLocaleString()}/{obj.valeur_cible.toLocaleString()} {obj.unite}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">🎯</span>
                <p className="mt-2">Aucun objectif défini</p>
                {access.canManage && (
                  <Link 
                    to={`/projects/${projectId}/finances/objectives`}
                    className="mt-2 inline-block text-indigo-600 text-sm hover:underline"
                  >
                    Créer un objectif
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Breakdown Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Revenue Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">💵 Répartition des Revenus</h3>
            {revenue_breakdown.length > 0 ? (
              <div className="space-y-3">
                {revenue_breakdown.map((cat, idx) => {
                  const percentage = summary.total_revenus > 0 ? ((cat.total / summary.total_revenus) * 100).toFixed(1) : 0;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                        style={{ backgroundColor: cat.couleur + '20' }}
                      >
                        {cat.icone}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium text-gray-800">{cat.nom}</span>
                          <span className="text-gray-600">{formatMoney(cat.total)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%`, backgroundColor: cat.couleur }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 w-12 text-right">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">📭</span>
                <p className="mt-2">Aucun revenu enregistré</p>
              </div>
            )}
          </div>

          {/* Expense Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">💸 Répartition des Dépenses</h3>
            {expense_breakdown.length > 0 ? (
              <div className="space-y-3">
                {expense_breakdown.map((cat, idx) => {
                  const percentage = summary.total_depenses > 0 ? ((cat.total / summary.total_depenses) * 100).toFixed(1) : 0;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                        style={{ backgroundColor: cat.couleur + '20' }}
                      >
                        {cat.icone}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium text-gray-800">{cat.nom}</span>
                          <span className="text-gray-600">{formatMoney(cat.total)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%`, backgroundColor: cat.couleur }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 w-12 text-right">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">📭</span>
                <p className="mt-2">Aucune dépense enregistrée</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="mt-6 bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">📋 Transactions Récentes</h3>
            <Link 
              to={`/projects/${projectId}/workspace/payments`}
              className="text-indigo-600 text-sm hover:underline"
            >
              Voir tout →
            </Link>
          </div>
          {recent_transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Description</th>
                    <th className="pb-3 font-medium">Catégorie</th>
                    <th className="pb-3 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recent_transactions.map((tx, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="py-3 text-sm text-gray-600">{formatDate(tx.date_transaction)}</td>
                      <td className="py-3">
                        <span className="text-gray-800">{tx.description || 'Sans description'}</span>
                        {tx.reference && (
                          <span className="ml-2 text-xs text-gray-500">#{tx.reference}</span>
                        )}
                      </td>
                      <td className="py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-sm">
                          {tx.categorie_icone} {tx.categorie_nom}
                        </span>
                      </td>
                      <td className={`py-3 text-right font-semibold ${tx.type === 'revenu' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'revenu' ? '+' : '-'}{formatMoney(tx.montant)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl">📭</span>
              <p className="mt-2">Aucune transaction</p>
              {access.canManage && (
                <Link 
                  to={`/projects/${projectId}/workspace/payments`}
                  className="mt-2 inline-block text-indigo-600 text-sm hover:underline"
                >
                  Voir les paiements
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectFinanceDashboard;
